import { atom, useAtomValue, useSetAtom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useCallback } from 'react'
import isEqual from 'react-fast-compare'
import type { DateText, HourMinute } from '~/lib/graphql/scalars'
import { visitTimes } from '~/lib/times'
import { scope } from './scope'
import type { SearchCriteria } from './validate'

export const visitTimeOptions: [label: string, value: string][] = [['未指定', '未指定']]
for (const time of visitTimes) {
  visitTimeOptions.push([time, time])
}

export const draftSearchCriteria$ = scope.atom<SearchCriteria | undefined>(
  undefined,
  'draftSearchCriteria$'
)
const draftVisitDate$ = selectAtom(draftSearchCriteria$, (s) => s?.visitDate)
draftVisitDate$.debugLabel = 'draftVisitDate$'
const draftVisitTime$ = selectAtom(draftSearchCriteria$, (s) => s?.visitTime)
draftVisitTime$.debugLabel = 'draftVisitTime$'
const draftVisitorsCount$ = selectAtom(draftSearchCriteria$, (s) => s?.visitorsCount)
draftVisitorsCount$.debugLabel = 'draftVisitorsCount$'
const memoizedDraft$ = selectAtom(draftSearchCriteria$, (s) => s, isEqual)
memoizedDraft$.debugLabel = 'memoizedDraft$'

export function useDraftSearchCriteria() {
  return useAtomValue(memoizedDraft$)
}

export function useSetDraftSearchCriteria() {
  return useSetAtom(draftSearchCriteria$)
}

export function useDraftVisitDate() {
  return useAtomValue(draftVisitDate$)
}

export function useDraftVisitTime() {
  return useAtomValue(draftVisitTime$)
}

export function useDraftVisitorsCountValue(): string | undefined {
  const selected = useAtomValue(draftVisitorsCount$)
  return selected ? String(selected) : undefined
}

export function useSetDraftVisitTime() {
  const set = useSetAtom(draftSearchCriteria$)
  return useCallback(
    (value: string) => {
      const visitTime = value !== '未指定' ? (value as HourMinute) : undefined
      set((current) => ({ ...current!, visitTime }))
    },
    [set]
  )
}

export const visitorsCountOptions = Array.from(Array(99)).map(
  (_, i) => [`${i + 1}名`, String(i + 1)] as [label: string, value: string]
)

export function useSetDraftVisitorsCount() {
  const set = useSetAtom(draftSearchCriteria$)
  return useCallback(
    (value: string) => {
      const visitorsCount = value !== '' ? parseInt(value, 10) : undefined
      set((current) => ({ ...current!, visitorsCount }))
    },
    [set]
  )
}

const setDraftVisitDate$ = atom<null, [DateText | undefined], void>(
  null,
  (_get, set, visitDate) => {
    set(draftSearchCriteria$, (current) => {
      // 手仕舞い済店舗の検索条件が当日以外の検索にも適用されてしまうことを防ぐため、
      // visitDate の変更に伴い、常に includeClosedInventory をリセットする
      const { visitDate: _, includeClosedInventory, ...rest } = current ?? {}
      return visitDate !== undefined ? { ...rest, visitDate } : rest
    })
  }
)
if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest
  const { createStore } = await import('jotai')
  describe('setDraftVisitDate', () => {
    test('日付を指定する', () => {
      const store = createStore()
      store.set(draftSearchCriteria$, {
        largeAreaCode: '07',
        mediumAreaCode: '002',
        includeClosedInventory: true,
      } as SearchCriteria)
      store.set(setDraftVisitDate$, '2024-07-17' as DateText)
      expect(store.get(draftSearchCriteria$)).toStrictEqual({
        largeAreaCode: '07',
        mediumAreaCode: '002',
        visitDate: '2024-07-17',
      })
    })
    test('日付を未指定にする', () => {
      const store = createStore()
      store.set(draftSearchCriteria$, {
        largeAreaCode: '07',
        mediumAreaCode: '002',
        visitDate: '2024-07-17',
        includeClosedInventory: true,
      } as SearchCriteria)
      store.set(setDraftVisitDate$, undefined)
      expect(store.get(draftSearchCriteria$)).toStrictEqual({
        largeAreaCode: '07',
        mediumAreaCode: '002',
      })
    })
  })
}

export function useSetDraftVisitDate() {
  const set = useSetAtom(setDraftVisitDate$)
  return useCallback(
    (visitDate: DateText | undefined) => {
      set(visitDate)
    },
    [set]
  )
}
